import { Injectable, Type, inject } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import {
    JsonType,
    FormFieldType,
    LookupStyle,
    Party,
    PartyStub,
} from '@wdx/clmi/clmi-swagger-gen';
import {
    IWrappedAvatarComponentConfig,
    IWrappedFormComponentConfig,
    IWrappedFormComponentProvider,
    ReactiveFormLayoutAndDefinition,
} from '@wdx/shared/infrastructure/form-framework';
import { Observable, map, of } from 'rxjs';
import { MoleculeAvatarComponent } from '../../../../components/molecules/molecule-avatar/molecule-avatar.component';
import { MoleculeReactiveInputLookupComponent } from '../../../../components/molecules/molecule-reactive-controls/_molecule-input-lookup/molecule-reactive-input-lookup.component';
import { MoleculeReactiveInputTagsComponent } from '../../../../components/molecules/molecule-reactive-controls/_molecule-input-tags/molecule-reactive-input-tags.component';
import { ReactiveFormDynamicDataService } from '../../../../shared/services';
import {
    encodedStringDeserialiser,
    passthroughOutputHandler,
} from '@wdx/shared/components/wdx-json-editor';
import {
    IWrappedWorkflowFormComponentProvider,
    WorkflowDesignerJsonEditorComponent,
} from '@wdx/clmi/features/workflow-designer';
import { FormAdvancedLookupComponent } from '../../../../shared/components/form-lookup-table/form-advanced-lookup.component';
import { FormJsonViewerComponent } from '../../../../shared/components/form-json-editor/form-json-editor.component';
import { GenericAdvancedLookupComponent } from '../../../../shared/components/form-lookup-table/generic-advanced-lookup/generic-advanced-lookup.component';

@Injectable()
export class WrappedFormControlService
    implements IWrappedFormComponentProvider
{
    public readonly hideErrorMessages = false;

    private dynamicDataService = inject(ReactiveFormDynamicDataService);

    private readonly customInputHandler = encodedStringDeserialiser;
    private readonly customOutputHandler = passthroughOutputHandler;

    getComponent$(
        formElement: ReactiveFormLayoutAndDefinition,
        formContext: any,
        formData: UntypedFormGroup,
        entityId: string,
        formControl?: AbstractControl,
    ): Observable<IWrappedFormComponentConfig> {
        if (formElement.tagCategory) {
            return this.dynamicDataService
                .getTagsForCategory(formElement.tagCategory)
                .pipe(
                    map((tags) => ({
                        component: MoleculeReactiveInputTagsComponent,
                        inputs: {
                            isMulti:
                                formElement.fieldType ===
                                FormFieldType.TagListMulti,
                            formInputData: formElement,
                            tags,
                        },
                    })),
                );
        }

        if (formElement.fieldType === FormFieldType.Json) {
            const workflow =
                formElement?.jsonDefinition.jsonType === JsonType.Workflow;
            const workflowInputs = workflow
                ? {
                      componentProvider: {
                          getComponent(): {
                              component: Type<any>;
                          } {
                              return {
                                  component: GenericAdvancedLookupComponent,
                              };
                          },
                      } as IWrappedWorkflowFormComponentProvider,
                  }
                : {};
            return of({
                component: workflow
                    ? WorkflowDesignerJsonEditorComponent
                    : FormJsonViewerComponent,
                inputs: {
                    customInputHandler: this.customInputHandler,
                    customOutputHandler: this.customOutputHandler,
                    formInputData: formElement,
                    formData,
                    formContext,
                    formControl,
                    entityId,
                    ...workflowInputs,
                },
            });
        }

        return of({
            component:
                formElement?.lookupStyle &&
                formElement.lookupStyle === LookupStyle.Advanced
                    ? FormAdvancedLookupComponent
                    : MoleculeReactiveInputLookupComponent,
            inputs: {
                isMulti:
                    formElement.fieldType === FormFieldType.LookupMulti ||
                    formElement.fieldType === FormFieldType.DocumentLookup,
                formInputData: formElement,
                formData,
                formContext: formContext,
            },
        });
    }

    getAvatarComponent$(
        data: Party | PartyStub,
    ): Observable<IWrappedAvatarComponentConfig> {
        return of({
            component: MoleculeAvatarComponent,
            inputs: {
                avatar: {
                    text: data.name,
                    avatarUrl: data.avatarUrl,
                    partyId: data.id,
                    partyType: data.partyType,
                    isAvatarInfoEnabled: true,
                },
            },
        });
    }
}
